import { cn } from "@/lib/utils";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Mention, MentionsInput as _MentionsInput } from "react-mentions";
import type { TextareaProps } from "../ui/textarea";

type User = {
  id: string;
  display: string;
  avatar?: string;
};

export type UserOptions = User[];

type Props = TextareaProps & {
  placeholder?: string;
  className?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  users: UserOptions;
  value: string;
  forceSuggestionsAboveCursor?: boolean;
};

export const MentionInput = ({
  placeholder: _placeholder,
  className,
  value,
  onChange,
  onBlur,
  onFocus,
  users,
  onKeyDown,
  forceSuggestionsAboveCursor,
}: Props) => {
  const { t } = useTranslation();

  const handleChange = (event: any, newValue: string) => {
    onChange?.(newValue);
  };

  const placeholder = _placeholder || t("mention.placeholder");

  return (
    <div className={cn("relative", className)}>
      <_MentionsInput
        onKeyDown={(event) => {
          if (onKeyDown) {
            onKeyDown(event as React.KeyboardEvent<HTMLTextAreaElement>);
          }
        }}
        value={value}
        onChange={handleChange}
        onBlur={onBlur}
        onFocus={onFocus}
        style={defaultStyle}
        placeholder={placeholder}
        forceSuggestionsAboveCursor={forceSuggestionsAboveCursor}
      >
        <Mention
          trigger="@"
          markup="@[__display__](__id__)"
          data={users}
          displayTransform={(id, display) => `@${display}`}
          appendSpaceOnAdd
          renderSuggestion={(
            suggestion,
            search,
            highlightedDisplay,
            index,
            focused,
          ) => (
            <div className={`user ${focused ? "focused" : ""}`}>
              {highlightedDisplay}
            </div>
          )}
          style={defaultMentionStyle}
        />
      </_MentionsInput>
    </div>
  );
};

const defaultStyle = {
  control: {
    backgroundColor: "hsl(var(--background))",
    fontSize: 14,
    fontWeight: "normal",
  },

  "&multiLine": {
    control: {
      minHeight: 80,
    },
    highlighter: {
      padding: 9,
      border: "1px solid transparent",
    },
    input: {
      padding: 9,
      border: "1px solid hsl(var(--input))",
      borderRadius: "0.5rem",
      color: "hsl(var(--foreground))",
    },
  },

  "&singleLine": {
    display: "inline-block",
    width: "100%",
    highlighter: {
      padding: 1,
      border: "1px solid transparent",
    },
    input: {
      padding: 1,
      border: "1px solid hsl(var(--input))",
      borderRadius: "0.5rem",
    },
  },

  suggestions: {
    list: {
      backgroundColor: "hsl(var(--background))",
      border: "1px solid hsl(var(--border))",
      borderRadius: "0.5rem",
      boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
      fontSize: 14,
      top: "100%",
      marginTop: "0.5rem",
    },
    item: {
      padding: "8px 12px",
      borderBottom: "1px solid hsl(var(--border))",
      "&focused": {
        backgroundColor: "hsl(var(--muted))",
        fontWeight: "600",
      },
    },
  },
};

const defaultMentionStyle = {
  backgroundColor: "hsl(var(--accent))",
  color: "hsl(var(--accent-foreground))",
};
